import { TBulkAnalytics } from '@types';
import { Option } from '@types';
import { format } from 'date-fns';

export const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const DATE_PRESET_OPTS: Option[] = [
	{ label: 'Today', value: 'today' },
	{ label: 'Tomorrow', value: 'tomorrow' },
	{ label: 'Yesterday', value: 'yesterday' },
	{ label: 'This Week', value: 'this_week' },
	{ label: 'Last 7 Days', value: 'last_7_days' },
	{ label: 'Next 7 Days', value: 'next_7_days' },
	{ label: 'This Month', value: 'this_month' },
	{ label: 'Last Month', value: 'last_month' },
	{ label: 'Last 30 Days', value: 'last_30_days' },
	{ label: 'This Year', value: 'this_year' },
	{ label: 'Last Year', value: 'last_year' },
	{ label: 'Date Range', value: 'date_range' },
	{ label: 'Specific Date', value: 'specific_date' },
];

export const resolveDatePreset = (preset: string): { start: string; end: string } => {
	const fmt = (d: Date): string => {
		const year = d.getFullYear();
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const day = String(d.getDate()).padStart(2, '0');
		return `${year}-${month}-${day}`;
	};
	const today = new Date();
	today.setHours(0, 0, 0, 0);

	switch (preset) {
	case 'today':
		return { start: fmt(today), end: fmt(today) };
	case 'tomorrow': {
		const d = new Date(today);
		d.setDate(d.getDate() + 1);
		return { start: fmt(d), end: fmt(d) };
	}
	case 'yesterday': {
		const d = new Date(today);
		d.setDate(d.getDate() - 1);
		return { start: fmt(d), end: fmt(d) };
	}
	case 'this_week': {
		const start = new Date(today);
		start.setDate(today.getDate() - today.getDay());
		return { start: fmt(start), end: fmt(today) };
	}
	case 'last_7_days': {
		const start = new Date(today);
		start.setDate(today.getDate() - 7);
		return { start: fmt(start), end: fmt(today) };
	}
	case 'next_7_days': {
		const end = new Date(today);
		end.setDate(today.getDate() + 7);
		return { start: fmt(today), end: fmt(end) };
	}
	case 'this_month': {
		const start = new Date(today.getFullYear(), today.getMonth(), 1);
		return { start: fmt(start), end: fmt(today) };
	}
	case 'last_month': {
		const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
		const end = new Date(today.getFullYear(), today.getMonth(), 0); // day-0 = last day of prev month
		return { start: fmt(start), end: fmt(end) };
	}
	case 'last_30_days': {
		const start = new Date(today);
		start.setDate(today.getDate() - 30);
		return { start: fmt(start), end: fmt(today) };
	}
	case 'this_year': {
		const start = new Date(today.getFullYear(), 0, 1);
		return { start: fmt(start), end: fmt(today) };
	}
	case 'last_year': {
		const start = new Date(today.getFullYear() - 1, 0, 1);
		const end = new Date(today.getFullYear() - 1, 11, 31);
		return { start: fmt(start), end: fmt(end) };
	}
	default:
		return { start: '', end: '' };
	}
};

export const deriveDatePreset = (report: TBulkAnalytics): string => {
	const { rule } = report;
	switch (rule.date_range_anchor) {
	case 'TOMORROW': return 'tomorrow';
	case 'THIS_YEAR': return 'this_year';
	case 'LAST_YEAR': return 'last_year';
	case 'LAST_MONTH': return 'last_month';
	case 'FIXED': return 'date_range';
	case 'LAST': {
		const n = rule.date_range_anchor_interval;
		const u = rule.date_range_anchor_interval_unit;
		if (n === 1 && u === 'DAY') return 'today';
		if (n === 7 && u === 'DAY') return 'last_7_days';
		if (n === 1 && u === 'WEEK') return 'this_week';
		if (n === 1 && u === 'MONTH') return 'this_month';
		if (n === 30 && u === 'DAY') return 'last_30_days';
		return 'last_7_days';
	}
	default: return '';
	}
};

export const deriveRepeatFields = (report: TBulkAnalytics) => {
	const { rule } = report;
	switch (rule.frequency) {
	case 'WEEKLY':
		return {
			repeat_period: 'custom',
			custom_period: 'weeks',
			custom_num: String(rule.frequency_interval ?? 1),
			custom_on: (rule.days_of_week ?? []).map((d) => DAY_NAMES[d] ?? ''),
		};
	case 'MONTHLY':
		return {
			repeat_period: `Every month on the ${format(new Date(), 'do')}`,
			custom_period: 'days',
			custom_num: '1',
			custom_on: [] as string[],
		};
	case 'YEARLY':
		return {
			repeat_period: `Every year on ${format(new Date(), 'LLLL do')}`,
			custom_period: 'days',
			custom_num: '1',
			custom_on: [] as string[],
		};
	case 'CUSTOM':
		return {
			repeat_period: 'custom',
			custom_period:
				rule.frequency_interval_unit === 'WEEK' ? 'weeks'
					: rule.frequency_interval_unit === 'MONTH' ? 'day of the month'
						: 'days',
			custom_num: String(rule.frequency_interval ?? 1),
			custom_on: [] as string[],
		};
	default:
		return {
			repeat_period: 'Every day', custom_period: 'days', custom_num: '1', custom_on: [] as string[]
		};
	}
};
